/*
 * Storage keys used by the inactivity rules feature:
 *   localStorage:   lastActivityTimestamp, presencePrompt, presencePromptClaim, presencePromptOutcome,
 *                   idleChainStage, idleChainStageAt, idleChainReturnedAt, statusChangedAt
 *   sessionStorage: reloadWithoutActivity, inactivityRulesLastBerlinDate
 */
import { useCallback, useEffect, useRef } from 'react';
import { signOut } from 'kits/authKit';
import { berlinDateString, isBerlinToday } from 'kits/berlinDateKit';
import IvicosStrings from 'kits/language/stringKit';
import { setLobbyRedirect } from 'features/Rooms/RoomList/helpers/lobbyRedirect';
import { persistOooTodayAndRedirectToLobby } from 'features/Rooms/RoomList/helpers/oooTodayLobbyRedirect';
import {
    IDLE_AWAY_TO_OOO_MS,
    IDLE_OOO_TO_PROMPT_MS,
    MIDNIGHT_INACTIVITY_MS,
    MIDNIGHT_SCHEDULE_MS,
    OOO_MANUAL_INACTIVITY_MS,
    PAGE_IDLE_TIMEOUT_MS,
    PRESENCE_PROMPT_SECONDS
} from 'shared-state/identity/constants/activityTimerConstants';
import {
    useAtWorkCustomPhrase,
    useAtWorkLocation,
    useAwayStatusMessage,
    useIdentityClaims,
    useOutOfOfficeAbsenceComment,
    useOutOfOfficeReturnDate,
    useOutOfOfficeReturnTime,
    useUserStatus
} from 'shared-state/identity/hooks';
import { getStoredStatus } from 'shared-state/identity/statusPersistence';
import type { UserStatus } from 'shared-state/identity/types';
import { useCurrentAreaId } from 'shared-state/location/hook';
import { useSendIdleNotification } from 'shared-state/notifications/hooks';
import {
    detectBerlinMidnight,
    evaluateInactivityRules,
    IInactivityTimers,
    initialLastSeenBerlinDate,
    isPromptShownInTab,
    legacyStageMigration,
    outcomeNavigation,
    resolvePromptTimeout,
    shouldDismissPromptForActivity,
    stageSyncAction
} from './inactivityRules';
import {
    AWAY_SELECTED_MANUALLY_KEY,
    claimPromptTimeout,
    clearIdleChainStage,
    clearOooSelectedManually,
    clearPrompt,
    IPresencePrompt,
    isAwaySelectedManually,
    isOooSelectedManually,
    isReconnectReloadDocument,
    markIdleChainReturned,
    markOooSelectedManually,
    OOO_SELECTED_MANUALLY_KEY,
    parsePromptOutcome,
    PRESENCE_PROMPT_OUTCOME_STORAGE_KEY,
    PRESENCE_PROMPT_STORAGE_KEY,
    readIdleChainStage,
    readIdleChainStageAt,
    readLastActivity,
    readLastBerlinDate,
    readManualFlag,
    readPrompt,
    removeManualFlag,
    setPromptVisibleInThisTab,
    writeIdleChainStage,
    writeLastActivity,
    writeLastBerlinDate,
    writePrompt,
    writePromptOutcome
} from './inactivityStorage';
import { createSerialRunner, onActivityRecorded } from './activitySignal';
import { usePresencePrompt } from './presencePromptState';

const CHECK_INTERVAL_MS = 15 * 1000;

const TIMERS: IInactivityTimers = {
    pageIdleMs: PAGE_IDLE_TIMEOUT_MS,
    awayToOooMs: IDLE_AWAY_TO_OOO_MS,
    oooToPromptMs: IDLE_OOO_TO_PROMPT_MS,
    manualOooMs: OOO_MANUAL_INACTIVITY_MS,
    midnightInactivityMs: MIDNIGHT_INACTIVITY_MS,
    stageSyncGraceMs: 4 * CHECK_INTERVAL_MS
};

/** Inside an area (not the lobby, not the visitor waiting area). */
export const isInArea = (areaId: string | undefined): boolean => !!areaId && areaId !== 'waiting-area';

// Identifies this tab when claiming a prompt timeout.
const TAB_ID = Math.random().toString(36).slice(2);

/**
 * The user's own status as IndexedDB holds it. Every tab persists there, so it is fresher than this tab's
 * Recoil atoms (which only follow other tabs after a restore). Falls back to Recoil when unavailable.
 */
async function readDecisionStatus(
    uid: string | undefined,
    fallbackStatus: UserStatus | undefined,
    fallbackReturnDate: Date | string | null | undefined
): Promise<{ status: UserStatus | undefined; returnDate: Date | string | null | undefined }> {
    if (!uid) return { status: fallbackStatus, returnDate: fallbackReturnDate };
    try {
        const stored = await getStoredStatus(uid);
        if (!stored) return { status: fallbackStatus, returnDate: fallbackReturnDate };
        return { status: stored.status, returnDate: stored.returnDate };
    } catch {
        return { status: fallbackStatus, returnDate: fallbackReturnDate };
    }
}

export interface IInactivityRulesState {
    prompt: IPresencePrompt | null;
    manualOooSignsOut: boolean;
    onConfirm: () => void;
    onTimeout: () => void;
}

/**
 * Client-owned inactivity rules: idle chain (away → OOO → prompt → lobby), manual OOO 4h prompt,
 * and the midnight prompt. See docs/status-manual-controll-design.md.
 */
export const useInactivityRules = (): IInactivityRulesState => {
    const [prompt, setPrompt] = usePresencePrompt();
    const [status, setStatus] = useUserStatus();
    const [, setAtWorkLocation] = useAtWorkLocation();
    const [, setAtWorkCustomPhrase] = useAtWorkCustomPhrase();
    const [, setAwayStatusMessage] = useAwayStatusMessage();
    const [returnDate, setReturnDate] = useOutOfOfficeReturnDate();
    const [, setReturnTime] = useOutOfOfficeReturnTime();
    const [, setAbsenceComment] = useOutOfOfficeAbsenceComment();
    const [identityClaims] = useIdentityClaims();
    const [currentAreaId] = useCurrentAreaId();
    const [, setSendIdleNotification] = useSendIdleNotification();

    // Latest values for interval callbacks (avoids stale closures)
    const latest = useRef({ status, returnDate, currentAreaId, uid: identityClaims?.uid as string | undefined });
    latest.current = { status, returnDate, currentAreaId, uid: identityClaims?.uid };

    // Midnight detection is per tab. The last seen Berlin date lives in sessionStorage so it survives the
    // reconnect reload after sleep (other page loads start from today, see initialLastSeenBerlinDate);
    // the ref only covers storage being unavailable.
    const lastBerlinDateRef = useRef(berlinDateString());
    const mountedAtRef = useRef(Date.now());
    const fakeMidnightIndexRef = useRef(0);
    const pendingMidnightRef = useRef<number | null>(null);

    const setAvailable = useCallback(() => {
        setStatus('available');
        setAtWorkLocation('office');
        setAtWorkCustomPhrase(undefined);
    }, [setStatus, setAtWorkLocation, setAtWorkCustomPhrase]);

    const setOutOfOfficeToday = useCallback(
        (todayStr: string, absenceComment: string) => {
            setStatus('out-of-office');
            setReturnDate(new Date(`${todayStr}T00:00:00Z`));
            setReturnTime(null);
            setAbsenceComment(absenceComment);
        },
        [setStatus, setReturnDate, setReturnTime, setAbsenceComment]
    );

    const detectMidnight = (now: number) => {
        if (MIDNIGHT_SCHEDULE_MS !== null) {
            // testing mode: a fake midnight every MIDNIGHT_SCHEDULE_MS since mount
            const index = Math.floor((now - mountedAtRef.current) / MIDNIGHT_SCHEDULE_MS);
            if (index > fakeMidnightIndexRef.current) {
                fakeMidnightIndexRef.current = index;
                pendingMidnightRef.current = mountedAtRef.current + index * MIDNIGHT_SCHEDULE_MS;
            }
            return;
        }
        const { nextSeenDate, midnightAt } = detectBerlinMidnight(now, readLastBerlinDate() ?? lastBerlinDateRef.current);
        lastBerlinDateRef.current = nextSeenDate;
        writeLastBerlinDate(nextSeenDate);
        if (midnightAt !== null) pendingMidnightRef.current = midnightAt;
    };

    const onTimeout = useCallback(async () => {
        const current = readPrompt();
        if (!current || Date.now() < current.deadline) return;
        // a tab that doesn't show this prompt (the lobby for midnight) never runs its action
        if (!isPromptShownInTab(current.rule, isInArea(latest.current.currentAreaId))) return;
        if (!claimPromptTimeout(TAB_ID, current.deadline)) return;
        clearPrompt();
        setPrompt(null);

        const { uid } = latest.current;
        const decisionStatus = await readDecisionStatus(uid, latest.current.status, latest.current.returnDate);
        const action = resolvePromptTimeout(current.rule, {
            status: decisionStatus.status,
            idleChainStage: readIdleChainStage(),
            oooSelectedManually: isOooSelectedManually(),
            returnDateIsToday: isBerlinToday(decisionStatus.returnDate)
        });

        // let the other tabs follow (lobby / sign out); they don't run the action themselves
        if (action !== 'none') writePromptOutcome({ rule: current.rule, action, deadline: current.deadline });

        switch (action) {
            case 'lobby-return-green':
                clearIdleChainStage();
                setLobbyRedirect('inactivity-detected', false);
                window.location.href = '/dashboard';
                break;
            case 'lobby-keep-status':
                setLobbyRedirect('ooo-inactivity', true);
                window.location.href = '/dashboard';
                break;
            case 'sign-out':
                clearOooSelectedManually();
                signOut();
                break;
            case 'midnight-keep-ooo':
                clearIdleChainStage();
                // the 4h manual-OOO rule applies to this OOO once the user is back in an area
                markOooSelectedManually();
                setLobbyRedirect('midnight-status-update', true);
                window.location.href = '/dashboard';
                break;
            case 'midnight-set-ooo-today': {
                if (!uid) break;
                const todayStr = berlinDateString();
                clearIdleChainStage();
                // the 4h manual-OOO rule applies to this OOO once the user is back in an area
                markOooSelectedManually();
                setOutOfOfficeToday(todayStr, '');
                void persistOooTodayAndRedirectToLobby(uid, todayStr, 'midnight-status-update');
                break;
            }
            case 'none':
                break;
        }
    }, [setPrompt, setOutOfOfficeToday]);

    const onConfirm = useCallback(() => {
        const current = readPrompt();
        writeLastActivity(Date.now());
        clearPrompt();
        setPrompt(null);
        if (current?.rule === 'idle-chain') {
            clearIdleChainStage();
            markIdleChainReturned(Date.now());
            setAvailable();
        }
    }, [setPrompt, setAvailable]);

    const tick = async () => {
        const now = Date.now();
        detectMidnight(now);

        const openPrompt = readPrompt();
        if (openPrompt) {
            const isInAreaNow = isInArea(latest.current.currentAreaId);
            if (!isPromptShownInTab(openPrompt.rule, isInAreaNow)) {
                // e.g. the lobby: the midnight prompt isn't shown here; activity here means the user is present
                if (shouldDismissPromptForActivity(openPrompt, isInAreaNow, readLastActivity(now), PRESENCE_PROMPT_SECONDS)) {
                    clearPrompt();
                    setPrompt(null);
                }
                return;
            }
            // backup for throttled background tabs where the modal's 1s interval lags
            if (now >= openPrompt.deadline) void onTimeout();
            return;
        }

        // decide from the status every tab persists, not this tab's possibly out-of-date Recoil copy
        const { status: currentStatus } = await readDecisionStatus(latest.current.uid, latest.current.status, latest.current.returnDate);
        // a prompt may have opened (e.g. in another tab) while IndexedDB was read
        if (readPrompt()) return;
        const { currentAreaId: areaId } = latest.current;
        const decision = evaluateInactivityRules(
            {
                now,
                lastActivity: readLastActivity(now),
                inArea: isInArea(areaId),
                status: currentStatus,
                idleChainStage: readIdleChainStage(),
                idleChainStageAt: readIdleChainStageAt(),
                oooSelectedManually: isOooSelectedManually(),
                promptOpen: false,
                pendingMidnightAt: pendingMidnightRef.current
            },
            TIMERS
        );
        // a pending midnight is evaluated exactly once (prompt opened or not applicable)
        pendingMidnightRef.current = null;

        switch (decision.type) {
            case 'clear-stage':
                clearIdleChainStage();
                break;
            case 'return-to-available':
                clearIdleChainStage();
                markIdleChainReturned(now);
                setAvailable();
                break;
            case 'set-away':
                writeIdleChainStage('away');
                setStatus('away');
                setAwayStatusMessage(IvicosStrings.awayCustomMessage);
                setSendIdleNotification(true);
                break;
            case 'set-ooo':
                writeIdleChainStage('ooo');
                setOutOfOfficeToday(berlinDateString(now), IvicosStrings.oooModalAbsenceOption);
                break;
            case 'open-prompt': {
                const nextPrompt: IPresencePrompt = { rule: decision.rule, deadline: now + PRESENCE_PROMPT_SECONDS * 1000 };
                writePrompt(nextPrompt);
                setPrompt(nextPrompt);
                break;
            }
            case 'none':
                break;
        }
    };

    const tickRef = useRef(tick);
    tickRef.current = tick;

    useEffect(() => {
        // restore a prompt that was open before a reload; one that expired while Campus was closed is dropped
        const storedPrompt = readPrompt();
        if (storedPrompt && storedPrompt.deadline <= Date.now()) {
            clearPrompt();
            setPrompt(null);
        } else {
            setPrompt(storedPrompt);
            // set before SocketProvider's page-load activity is recorded (child effects run first)
            setPromptVisibleInThisTab(!!storedPrompt && isPromptShownInTab(storedPrompt.rule, isInArea(latest.current.currentAreaId)));
        }
        writeLastBerlinDate(initialLastSeenBerlinDate(readLastBerlinDate(), berlinDateString(), isReconnectReloadDocument()));

        const runCheck = createSerialRunner(
            () => tickRef.current(),
            (err) => console.warn('[InactivityRules] check failed:', err)
        );
        const id = setInterval(runCheck, CHECK_INTERVAL_MS);
        // check right away on activity, so returning from Away/Gray is immediate instead of waiting for the next interval
        const unsubscribe = onActivityRecorded(runCheck);
        return () => {
            clearInterval(id);
            unsubscribe();
        };
    }, [setPrompt]);

    useEffect(() => {
        // one-time adoption of away/OOO statuses set by the old server idle pipeline (manual flag left at "false")
        let isCancelled = false;
        void (async () => {
            const { status: migrationStatus } = await readDecisionStatus(latest.current.uid, latest.current.status, latest.current.returnDate);
            if (isCancelled) return;
            const migration = legacyStageMigration(migrationStatus, readManualFlag(AWAY_SELECTED_MANUALLY_KEY), readManualFlag(OOO_SELECTED_MANUALLY_KEY));
            if (!migration) return;
            writeIdleChainStage(migration.stage);
            removeManualFlag(migration.clearFlag === 'away' ? AWAY_SELECTED_MANUALLY_KEY : OOO_SELECTED_MANUALLY_KEY);
        })();
        return () => {
            isCancelled = true;
        };
    }, []);

    useEffect(() => {
        // keep all tabs' modals and idle-chain status in sync (open/confirm/timeout/stage change in another tab)
        const onStorage = (event: StorageEvent) => {
            if (event.key === PRESENCE_PROMPT_STORAGE_KEY || event.key === null) setPrompt(readPrompt());

            if (event.key === PRESENCE_PROMPT_OUTCOME_STORAGE_KEY) {
                const outcome = parsePromptOutcome(event.newValue);
                if (outcome) {
                    setPrompt(null);
                    const navigation = outcomeNavigation(outcome.action);
                    if (navigation.kind === 'sign-out') {
                        signOut();
                    } else if (navigation.kind === 'lobby' && !window.location.pathname.startsWith('/dashboard')) {
                        setLobbyRedirect(navigation.reason, navigation.preserveStatus);
                        window.location.href = '/dashboard';
                    }
                }
                return;
            }

            const syncAction = stageSyncAction(
                event.key,
                event.newValue,
                latest.current.status,
                readIdleChainStage(),
                isOooSelectedManually(),
                isAwaySelectedManually()
            );
            switch (syncAction) {
                case 'set-away':
                    setStatus('away');
                    setAwayStatusMessage(IvicosStrings.awayCustomMessage);
                    break;
                case 'set-ooo-today':
                    setOutOfOfficeToday(berlinDateString(), IvicosStrings.oooModalAbsenceOption);
                    break;
                case 'set-available':
                    setAvailable();
                    break;
                case 'none':
                    break;
            }
        };
        window.addEventListener('storage', onStorage);
        return () => window.removeEventListener('storage', onStorage);
    }, [setPrompt, setStatus, setAwayStatusMessage, setOutOfOfficeToday, setAvailable]);

    // the midnight prompt is never shown in the lobby
    const shownPrompt = prompt && isPromptShownInTab(prompt.rule, isInArea(currentAreaId)) ? prompt : null;

    useEffect(() => {
        setPromptVisibleInThisTab(shownPrompt !== null);
    }, [shownPrompt]);

    useEffect(() => () => setPromptVisibleInThisTab(false), []);

    return {
        prompt: shownPrompt,
        manualOooSignsOut: shownPrompt?.rule === 'manual-ooo' && !isBerlinToday(returnDate),
        onConfirm,
        onTimeout
    };
};
